import React, { useEffect, useMemo, useState } from 'react';
import { getProjectBySlug, setUnlocked, verifyPin } from '@/lib/access';
import { useTranslation } from 'react-i18next';

interface PinGateProps {
  slug: string;
  onSuccess: () => void;
  projectTitle?: string;
  message?: string;
}

const PinGate: React.FC<PinGateProps> = ({ slug, onSuccess, projectTitle, message }) => {
  const { t } = useTranslation();
  const project = useMemo(() => getProjectBySlug(slug), [slug]);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resetPin = () => {
    setPin('');
  };

  const handleDigit = async (digit: string) => {
    if (pin.length >= 4) return;
    if (error) setError(null);
    const next = `${pin}${digit}`;
    setPin(next);
    if (next.length === 4) {
      if (project && verifyPin(project, next)) {
        await setUnlocked(slug, 'pin');
        setError(null);
        resetPin();
        onSuccess();
      } else {
        setError(t('pinGate.invalid'));
        resetPin();
      }
    }
  };

  const handleBackspace = () => {
    if (pin.length === 0) return;
    setPin(pin.slice(0, -1));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (/^[0-9]$/.test(event.key)) {
        handleDigit(event.key);
      } else if (event.key === 'Backspace') {
        handleBackspace();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pin]);

  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#E0E0E0] flex items-center justify-center px-4 safe-area-top safe-area-bottom">
      <div className="w-full max-w-md bg-[#232323]/90 border border-white/5 rounded-2xl p-8 shadow-2xl backdrop-blur-md transition-opacity duration-200">
        <div className="text-center space-y-2 mb-6">
          <p className="text-sm uppercase tracking-[0.2em] text-[#A0A0A0]">{t('pinGate.title')}</p>
          <h1 className="text-2xl font-semibold">{projectTitle || t('pinGate.subtitle')}</h1>
          {message && <p className="text-xs text-[#B0B0B0]">{message}</p>}
        </div>

        <div className="flex items-center justify-center gap-3 mb-6">
          {[0, 1, 2, 3].map((index) => (
            <span
              key={index}
              className={`h-3 w-3 rounded-full border border-[#666] ${pin.length > index ? 'bg-[#C8102E] border-[#C8102E]' : ''}`}
            />
          ))}
        </div>

        {error && <p className="text-center text-sm text-red-400 mb-4">{error}</p>}

        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleDigit(String(num))}
              className="h-14 rounded-xl bg-[#1f1f1f] border border-[#2f2f2f] text-lg font-semibold hover:border-[#C8102E] hover:bg-[#2a2a2a] transition-colors"
            >
              {num}
            </button>
          ))}
          <div />
          <button
            onClick={() => handleDigit('0')}
            className="h-14 rounded-xl bg-[#1f1f1f] border border-[#2f2f2f] text-lg font-semibold hover:border-[#C8102E] hover:bg-[#2a2a2a] transition-colors"
          >
            0
          </button>
          <button
            onClick={handleBackspace}
            className="h-14 rounded-xl bg-[#1f1f1f] border border-[#2f2f2f] text-lg font-semibold hover:border-[#C8102E] hover:bg-[#2a2a2a] transition-colors"
            aria-label={t('pinGate.delete')}
          >
            Del
          </button>
        </div>
      </div>
    </div>
  );
};

export default PinGate;
